import MarketingPage from "@/components/public/MarketingPage";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, Users, FileText, Database, Bell,
         Scale, Accessibility, Server, ArrowUpRight } from "lucide-react";

/* ─────────── /security — factual sections only ─────────── */
const SECURITY_SECTIONS = [
    { icon: Lock,     label: "Identity & Authentication",
      body: "Every login uses bcrypt-hashed passwords and optional TOTP-based MFA. Sessions are short-lived, HTTP-only, and rotate on privilege change. Brute-force protection is applied per-email." },
    { icon: Database, label: "Data Protection",
      body: "Records are stored in per-tenant collections. Documents are stored on isolated storage with signed URLs. VAULT never stores CVV, PIN or OTP." },
    { icon: Server,   label: "Workspace Isolation",
      body: "Every read and write is stamped with a tenant_id. The unmatchable-tenant fallback (2026-07-28) means legacy users with no tenant see zero data until an admin assigns them a workspace." },
    { icon: Users,    label: "Household Permissions",
      body: "Household visibility (Private, Shared Summary, Shared Full, Selected Members) is enforced server-side on every read. Private records never appear in another member's household view." },
    { icon: FileText, label: "Documents",
      body: "Documents can be marked private per-record. Uploaded files are scoped to the current tenant and never returned across workspaces." },
    { icon: Bell,     label: "Sessions",
      body: "Active sessions are visible in Settings → Security. Step-up authentication is required for sensitive changes like MFA rotation and password reset." },
    { icon: ShieldCheck, label: "Privacy Controls",
      body: "Hide-Values instantly redacts every balance across the app. Household privacy presets let couples and families pick sensible defaults on day one." },
    { icon: Database, label: "Backups",
      body: "Managed database backups are handled by the platform. VAULT admins can trigger a data export at any time from Settings → Data Export." },
    { icon: Scale,    label: "Responsible Disclosure",
      body: <>Report vulnerabilities to <a className="text-amber-800 font-semibold" href="mailto:security@vaultbypd.com">security@vaultbypd.com</a>. See <Link className="text-amber-800 font-semibold" to="/security/report-vulnerability">the disclosure page</Link> for scope.</> },
];

export function SecurityPublicPage() {
    return (
        <MarketingPage
            slug="security"
            title="Security at VAULT"
            description="Factual security posture — identity, data protection, workspace isolation and privacy controls implemented today."
            canonical="/security"
            hero="Security is a habit, not a headline."
            sub="Everything below is implemented today — no unverifiable marketing claims and no bank-grade vocabulary."
        >
            <section className="max-w-4xl mx-auto px-6 pb-16" data-testid="security-sections">
                <div className="space-y-2">
                    {SECURITY_SECTIONS.map(s => {
                        const Icon = s.icon;
                        return (
                            <div key={s.label} className="rounded-xl border border-stone-200 bg-white p-4 flex items-start gap-3"
                                 data-testid={`security-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                <div className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
                                    <Icon size={15} className="text-stone-700" />
                                </div>
                                <div>
                                    <div className="text-[14px] font-semibold text-stone-900">{s.label}</div>
                                    <div className="text-[12.5px] text-stone-600 mt-1 leading-relaxed">{s.body}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>
        </MarketingPage>
    );
}

/* ─────────── /trust — index card grid ─────────── */
const TRUST_CARDS = [
    { icon: Lock,       label: "Security",         to: "/security",       body: "Identity, data, isolation, sessions." },
    { icon: ShieldCheck,label: "Privacy",          to: "/privacy",        body: "What we collect, how we use it, and what we never do." },
    { icon: Database,   label: "Data Handling",    to: "/privacy-centre", body: "How records are stored, retained and exported." },
    { icon: Server,     label: "Subprocessors",    to: "/subprocessors",  body: "Every third party VAULT talks to on your behalf." },
    { icon: Bell,       label: "Status",           to: "/status",         body: "Live component statuses and past incidents." },
    { icon: Accessibility, label: "Accessibility", to: "/accessibility",  body: "Colour contrast, keyboard access, screen-reader compatibility." },
    { icon: Scale,      label: "Legal",            to: "/terms",          body: "Terms, refund policy, acceptable-use and financial disclaimer." },
    { icon: FileText,   label: "Responsible Disclosure", to: "/security/report-vulnerability", body: "Reporting scope and expected response times." },
];

export function TrustCentrePublicPage() {
    return (
        <MarketingPage
            slug="trust"
            title="Trust Centre"
            description="Security, privacy, data handling, subprocessors, status, accessibility and legal — all in one place."
            canonical="/trust"
            hero="Every trust question, in one calm place."
            sub="How VAULT is built, who VAULT talks to, and what VAULT promises — with real links to the source of truth."
        >
            <section className="max-w-4xl mx-auto px-6 pb-16" data-testid="trust-cards">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {TRUST_CARDS.map(c => {
                        const Icon = c.icon;
                        return (
                            <Link key={c.label} to={c.to}
                                  className="rounded-xl border border-stone-200 bg-white p-4 flex items-start gap-3 hover:border-stone-500 hover:shadow-md transition-all"
                                  data-testid={`trust-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                <div className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
                                    <Icon size={15} className="text-stone-700" />
                                </div>
                                <div className="flex-1">
                                    <div className="text-[14px] font-semibold text-stone-900 flex items-center gap-1">
                                        {c.label} <ArrowUpRight size={11} className="text-stone-400" />
                                    </div>
                                    <div className="text-[12px] text-stone-600 mt-0.5">{c.body}</div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>
        </MarketingPage>
    );
}
